// =============================================================================
// Base API Client
// Injects access token and handles common request patterns
// =============================================================================

import { env } from '../env';
import { store } from '@/store/store';
import { refreshToken, logoutUser, getTokenExpiry } from '@/store/authSlice';

/**
 * Returns a fresh access token from the Redux store.
 * If the token is expired or missing, triggers a refresh.
 */
export async function getAuthToken(): Promise<string | null> {
  const token = store.getState().auth.accessToken;

  if (token) {
    const expiry = getTokenExpiry(token);
    if (expiry > Date.now() + 10_000) {
      return token;
    }
  }

  // Token expired or missing — try refresh
  const result = await store.dispatch(refreshToken()).unwrap();
  return result?.accessToken ?? null;
}

export function forceLogout(): void {
  store.dispatch(logoutUser());
}

const BASE_URL = env.api.baseUrl;

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    console.warn('Failed to retrieve access token');
  }

  return headers;
}


export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }

  // Handle 202 Accepted and 204 No Content
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return {} as T;
  }

  return response.json() as Promise<T>;
}


export async function apiDownload(path: string, fileName: string): Promise<void> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' });
  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function apiGetBlob(path: string): Promise<Blob> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' });
  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }

  return response.blob();
}

export class ApiError extends Error {
  status: number;
  body: string;

  constructor(status: number, body: string) {
    super(`API Error ${status}: ${body}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}
